import React, {FunctionComponent, PropsWithChildren, ReactNode, useEffect, useState} from 'react';
import {
   ErrorDetails,
   getEnvironment,
   handleError,
   initializeErrorHandler,
   installWindowOnErrorHandler
} from '@unifocus/js-utils';
import {ErrorReport} from './ErrorReport';

interface ErrorHandlingOptions {
   errorDetails?: ErrorDetails | null;
   onErrorAcknowledged?: () => void | null;
}

export const showErrorDialog = (error: Error): Promise<any> => {
   return new Promise((resolve, reject) => {
      const environment = getEnvironment();
      const errorAsAny = error as any;

      const errorDetails: ErrorDetails = {
         errorText: error.message,
         applicationId: environment.applicationId,
         buildNo: environment.buildNo,
         timeStamp: new Date().toISOString(),
         message: errorAsAny.response ? 'AJAX Error' : 'Error',
         errorCode: errorAsAny.response && errorAsAny.response.status ? errorAsAny.response.status.toString() : undefined,
         requestMethod: (errorAsAny.config && errorAsAny.config.method) ? errorAsAny.config.method.toUpperCase() : undefined,
         requestUrl: errorAsAny.config ? errorAsAny.config.url : undefined,
         userAgent: window.navigator.userAgent,
         correlationId: errorAsAny.response && errorAsAny.response.headers ? errorAsAny.response.headers['correlationId'] : undefined,
         partnerCode: environment.partnerCode,
         databaseId: environment.databaseId ? environment.databaseId.toString() : undefined,
         propertyId: environment.propertyId ? environment.propertyId.toString() : undefined,
         stackTrace: errorAsAny.response ? errorAsAny.response.data : undefined
      };

      handleError(errorDetails, resolve as any);
   });
};

export const withErrorHandler = <P extends unknown>(WrappedComponent: React.ComponentClass<P> | FunctionComponent<P>): React.ComponentClass<P> => {
   return class extends React.Component<P> {
      render(): ReactNode {
         return (
            <ErrorHandler>
               <WrappedComponent
                  {...this.props}
               />
            </ErrorHandler>
         );
      }
   };
};

export const ErrorHandler = (props: PropsWithChildren<any>) => {
   const [errorHandlingState, setErrorHandlingState] = useState({} as ErrorHandlingOptions);

   useEffect(() => {
      installWindowOnErrorHandler();

      initializeErrorHandler((error: ErrorDetails, onErrorAcknowledged?: () => void): void => {
         setErrorHandlingState({
                                  errorDetails: error,
                                  onErrorAcknowledged: () => {
                                     if (onErrorAcknowledged) {
                                        onErrorAcknowledged();
                                     }

                                     setErrorHandlingState({
                                                              errorDetails: null,
                                                              onErrorAcknowledged: () => {
                                                                 // do nothing
                                                              }
                                                           });
                                  }
                               });
      });
   },[]);

   return (
      <>
         <ErrorComponent
            errorDetails={errorHandlingState.errorDetails}
            onErrorAcknowledged={errorHandlingState.onErrorAcknowledged}
         />
         {props.children}
      </>
   );
};

const ErrorComponent = (props: ErrorHandlingOptions) => {
   if (props.errorDetails) {
      return (
         <ErrorReport
            errorDetails={props.errorDetails}
            onErrorAcknowledged={props.onErrorAcknowledged}
         />
      );
   }

   return null;
};
